import type { PillTone } from '../../../components/ui/Pill';

/** How a card in the pack reads to the operator. `engagement_status` is the
 *  pool item's own lifecycle value; membership in the pack is independent of
 *  it, so every one of these can show up in the pack contents. */
export interface EngagementStatusBadge {
  label: string;
  tone: PillTone;
  /** Why the card is (or is not) in play — shown as the pill's tooltip. */
  hint: string;
}

const BADGES: Record<string, EngagementStatusBadge> = {
  listed: { label: 'In play', tone: 'success', hint: 'In the pool and can be pulled' },
  on_hands: { label: 'With player', tone: 'info', hint: 'Pulled by a player' },
  reserved: { label: 'Reserved', tone: 'warning', hint: 'Held by a round in progress' },
  buyback: { label: 'Bought back', tone: 'neutral', hint: 'The player sold it back' },
  redeemed: { label: 'Delivered', tone: 'neutral', hint: 'Shipped to the player' },
  unavailable: { label: 'Unavailable', tone: 'warning', hint: 'Cannot be pulled right now' },
  burned: { label: 'Burned', tone: 'error', hint: 'Taken out of circulation' },
};

function humanise(status: string): string {
  const words = status.replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Unknown';
}

/** An operator may never learn that inventory is split into virtual/offline and
 *  physical. The fallback below prints a status NAME straight to the screen, so
 *  a future backend status carrying the stock kind in its name would disclose it
 *  through this door. Such a name is shown as the neutral fallback instead. */
const DISCLOSING_STATUS = /virtual|offline|physical/i;

/** Unknown values are shown, not hidden: a status the console has never heard
 *  of still explains why the card is not in play — unless its name would
 *  disclose the stock kind, in which case only the neutral label is shown. */
export function engagementStatusBadge(status: string | null | undefined): EngagementStatusBadge {
  if (!status) return { label: 'Unknown', tone: 'neutral', hint: '' };
  const known = BADGES[status];
  if (known) return known;
  if (DISCLOSING_STATUS.test(status)) return { label: 'Not in play', tone: 'neutral', hint: '' };
  return { label: humanise(status), tone: 'neutral', hint: '' };
}
